// src/pages/BillingTable.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaColumns, FaExpand, FaCompress } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { MdOutlineSegment } from "react-icons/md";
import { IoMdClose } from "react-icons/io";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa6";

import LoadingAnimation from "../components/LoadingAnimation";
import ModifyColumns from "./Tools/ModifyColumns";
import Download from "./Components/Download";
import {
  FilterComponent,
  getFilteredData,
  renderTableCell,
  renderTableTotal,
} from "./config/tableHelper"; // Adjust path if needed
import axios from "axios";
 

function getFormattedDateTime(isoString) {
  const dateObj = new Date(isoString);

  // Format date: MM/DD/YYYY
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const year = dateObj.getFullYear();
  const formattedDate = `${month}/${day}/${year}`;

  // Format time: HH:MM:SS AM/PM
  let hours = dateObj.getHours();
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  const formattedTime = `${hours}:${minutes}:${seconds} ${ampm}`;

  return {
    date: formattedDate,
    time: formattedTime,
  };
}

const baseColumns = [
  {
    id: 2,
    title: "Date",
    key: "createdAt",
    visible: true,
    type: "string",
    category: "Recommended",

    locked: true,
    isLocked: true,
  },
  {
    id: 22,
    title: "Time",
    key: "createdAtTime",
    visible: true,
    type: "string",
    category: "Recommended",

    locked: true,
    isLocked: true,
  },
  {
    id: 1,
    title: "Invoice ID",
    key: "id",
    visible: false,
    type: "string",
    sticky: true,
    category: "Recommended",

    locked: true,
    isLocked: true,
  },
  {
    id: 5,
    title: "Status",
    key: "status",
    visible: true,
    type: "string",
    category: "Recommended",

    locked: true,
    isLocked: true,
  },
  {
    id: 11,
    title: "Email",
    key: "email",
    visible: true,
    type: "string",
    sticky: true,
    category: "Recommended",

    locked: true,
    isLocked: true,
  },
  {
    id: 12,
    title: "Transaction ID",
    key: "transactionId",
    visible: true,
    type: "string",
    sticky: true,
    category: "Recommended",
  },
  {
    id: 4,
    title: "Amount",
    key: "amount",
    visible: true,
    type: "number",
    category: "Recommended",
  },
  {
    id: 13,
    title: "Currency",
    key: "currency",
    visible: false,
    type: "string",
    sticky: true,
    category: "Recommended",
  },
  {
    id: 14,
    title: "Order ID",
    key: "orderId",
    visible: false,
    type: "string",
    sticky: true,
    category: "Recommended",
  },
];
const fetchTransactionHistory = async (
  setColumns,
  setData,
  setTotal,
  setError
) => {
  axios
    .post("https://api.confidanto.com/subscription/get-transaction-details", {
      email: "santoshsakre21@gmail.com",
    })
    .then((res) => {
      if (res.data) {
        let newData = res.data.map((item) => {
          let { date, time } = getFormattedDateTime(item.createdAt);
          item.createdAt = date;
          item.createdAtTime = time;
          item.statuss = item.status;
          return item;
        });
        setColumns(baseColumns);
        setData(newData);
      }
    })
    .catch((err) => {
      console.error("Error fetching default billing data:", err);
      setError(err);
    })
    .finally((fin) => {});
};

const BillingTable = () => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterArray, setFilterArray] = useState([]);

  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState({});
  const [error, setError] = useState(null);

  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [tableVisible, setTableVisible] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [totalShow, setTotalShow] = useState(true);

  const [segmentType, setSegmentType] = useState("default");
  const [isSegmentListVisible, setIsSegmentListVisible] = useState(false);
  const [random, setRandom] = useState(Math.random()); // To trigger refetch

  // Refs for closing menus on outside click
  const SegmentRef = useRef(null);
  const DownloadRef = useRef(null);
  const ColumnRef = useRef(null);

  // Data fetching useEffect with switch statement
  useEffect(() => {
    const loadBillingData = async () => {
      setData([]); // Clear data to show loading
      setError(null);

      try {
        switch (segmentType) {
          case "segment_device":
            break;

          default: // 'default' case
            await fetchTransactionHistory(
              setColumns,
              setData,
              setTotal,
              setError
            );
            break;
        }
      } catch (error) {
        console.error("Error in useEffect data loading sequence:", error);
        setError("An unexpected error occurred during data loading.");
      }
    };

    loadBillingData();
  }, [random, segmentType]);

  // Function to change segment and trigger data refetch
  const changeSegment = (segment) => {
    setSegmentType(segment);
    setRandom(Math.random()); // Trigger useEffect
    setIsSegmentListVisible(false);
  };

  // Handle clicks outside of menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (SegmentRef.current && !SegmentRef.current.contains(event.target))
        setIsSegmentListVisible(false);
      if (DownloadRef.current && !DownloadRef.current.contains(event.target))
        setShowDownloadOptions(false);
      if (ColumnRef.current && !ColumnRef.current.contains(event.target))
        setShowColumnsMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Central function to close all popups/menus
  const closeModalBoxes = (type) => {
    setShowColumnsMenu(false);
    setShowDownloadOptions(false);
    setIsSegmentListVisible(false);

    if (type === "Segment") setIsSegmentListVisible((p) => !p);
    else if (type === "Download") setShowDownloadOptions((p) => !p);
    else if (type === "Column") setShowColumnsMenu((p) => !p);
  };

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  // Sorting Logic
  const [currentOrderType, setCurrentOrderType] = useState(true);
  const [currentOrderVariable, setCurrentOrderVariable] = useState(null);

  const changeOrderType = (key) => {
    const newOrderType =
      key !== currentOrderVariable ? true : !currentOrderType;
    setCurrentOrderType(newOrderType);
    setCurrentOrderVariable(key);

    const sortedData = [...data].sort((a, b) => {
      const valA = a[key];
      const valB = b[key];

      if (valA == null) return 1;
      if (valB == null) return -1;

      let comparison = 0;
      if (typeof valA === "number" && typeof valB === "number") {
        comparison = valA - valB;
      } else {
        comparison = String(valA).localeCompare(String(valB));
      }
      return newOrderType ? comparison : -comparison;
    });
    setData(sortedData);
  };

  const filteredData = useMemo(() => {
    return getFilteredData(Array.isArray(data) ? data : [], filterArray);
  }, [data, filterArray]);

  const removeFilterFromArray = (id) =>
    setFilterArray((prev) => prev.filter((fil) => fil.id !== id));

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const handlePageChange = (newPage) => setCurrentPage(newPage);

  // --- JSX RENDER ---
  return (
    <div
      className={`flex h-screen bg-white ${
        isFullScreen
          ? "fixed top-0 left-0 w-full h-full z-50 overflow-x-scroll"
          : ""
      }`}
    >
      <main className="flex-grow p-6">
        <div>
          <h2 class="text-2xl font-semibold text-left text-blue-600 flex flex-col my-4 items-start">
            Payment History
          </h2>
        </div>
        {/* Toolbar */}
        <div className="flex justify-end items-center mb-4 space-x-2">
          {true && ( // Changed from false to true to show the filter component
            <FilterComponent
              columns={columns}
              data={data}
              filterArray={filterArray}
              setFilterArray={setFilterArray}
            />
          )}
          {/* Segment Button & Menu */}
          {false && (
            <div className="relative" ref={SegmentRef}>
              <button
                className={`px-4 py-2 rounded flex flex-col justify-center items-center ${
                  segmentType !== "default"
                    ? "bg-blue-100 text-blue-600"
                    : "bg-transparent text-gray-600 hover:bg-slate-100"
                }`}
                onClick={() => closeModalBoxes("Segment")}
              >
                <MdOutlineSegment />
                <h1>Segment</h1>
              </button>
              {isSegmentListVisible && (
                <div className="absolute z-20 w-56 bg-white shadow-lg rounded-lg mt-2 p-4 border border-gray-200 right-0">
                  <div
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => changeSegment("default")}
                  >
                    Reset
                  </div>
                  <p className="p-2 text-sm text-gray-400">By</p>
                  <ul className="mx-2">
                    <li
                      className={`p-2 capitalize cursor-pointer ${
                        segmentType === "segment_device"
                          ? "bg-blue-100 text-blue-600"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => changeSegment("segment_device")}
                    >
                      Device
                    </li>
                    <li
                      className={`p-2 capitalize cursor-pointer ${
                        segmentType === "segment_network"
                          ? "bg-blue-100 text-blue-600"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => changeSegment("segment_network")}
                    >
                      Payment Network
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
          {/* Other Toolbar Buttons */}
          <Download
            columns={columns}
            data={filteredData}
            closeModalBoxes={closeModalBoxes}
            showDownloadOptions={showDownloadOptions}
            setShowDownloadOptions={setShowDownloadOptions}
            DownloadRef={DownloadRef}
          />
          <button
            className={`px-4 py-2 rounded flex flex-col justify-center items-center ${
              isFullScreen
                ? "bg-blue-100 text-blue-600"
                : "bg-transparent text-gray-600 hover:bg-slate-100"
            }`}
            onClick={toggleFullScreen}
          >
            {isFullScreen ? <FaCompress /> : <FaExpand />}
            <h2>{isFullScreen ? "Collapse" : "Expand"}</h2>
          </button>
          <div className="relative" ref={ColumnRef}>
            <button
              className="bg-transparent text-gray-600 px-4 py-2 rounded items-center hover:bg-slate-100"
              onClick={() => closeModalBoxes("Column")}
            >
              <FaColumns className="ml-5" /> Columns
            </button>
            {showColumnsMenu && (
              <ModifyColumns
                columns={columns}
                setColumns={setColumns}
                setTableVisible={setTableVisible}
                setShowColumnsMenu={setShowColumnsMenu}
                setTotalShow={setTotalShow}
              />
            )}
          </div>
        </div>
        {/* Table Area */}
        {tableVisible && (
          <div className="overflow-auto h-full">
            {/* Filter Pills */}
            {filterArray.length > 0 && (
              <div className="w-full flex items-center p-2 mb-2 border-b">
                <div className="flex flex-wrap gap-2">
                  {filterArray.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 border border-gray-400 rounded-lg bg-gray-100"
                    >
                      <span className="mr-2 text-sm">
                        {item.title} {item.sign}{" "}
                        {Array.isArray(item.value)
                          ? item.value.join(", ")
                          : item.value}
                      </span>
                      <button
                        onClick={() => removeFilterFromArray(item.id)}
                        className="hover:text-red-500"
                      >
                        <IoMdClose />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setFilterArray([])}
                  className="ml-auto text-sm text-red-600 hover:underline p-2"
                >
                  Clear All
                </button>
              </div>
            )}
            {/* Table or Loading/Error State */}
            {error ? (
              <div className="text-center text-red-500 p-4">{error}</div>
            ) : filteredData.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white rounded-lg shadow-md">
                    <thead className="bg-[#283593] text-white">
                      <tr className="text-sm leading-normal">
                        {columns
                          .filter((col) => col.visible)
                          .map((col) => (
                            <th
                              key={col.key}
                              className={`py-3 px-3 border-r-2 border-gray-300 text-left ${
                                col.sticky
                                  ? "sticky left-0 bg-[#283593] z-10 shadow-md"
                                  : ""
                              }`}
                            >
                              <button
                                className="flex items-center justify-between w-full"
                                onClick={() => changeOrderType(col.key)}
                              >
                                <h2 className="whitespace-nowrap font-bold">
                                  {col.title}
                                </h2>
                                {currentOrderVariable === col.key && (
                                  <IoIosArrowDown
                                    className={`ml-2 transform transition-transform duration-300 ${
                                      currentOrderType
                                        ? "rotate-180"
                                        : "rotate-0"
                                    }`}
                                  />
                                )}
                              </button>
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm font-light">
                      {currentData.map((item, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-200 hover:bg-gray-100"
                        >
                          {columns
                            .filter((col) => col.visible)
                            .map((col) => (
                              <td
                                key={col.key}
                                className={`py-3 px-6 border-r-2 border-gray-200  whitespace-nowrap
                                  ${
                                    col.key === "status"
                                      ? "text-center flex justify-center items-center"
                                      : "text-left"
                                  }
                                  `}
                              >
                                {renderTableCell(col, item)}
                              </td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                    {totalShow && (
                      <tfoot>
                        <tr className="font-bold text-gray-700 bg-gray-100">
                          {columns
                            .filter((col) => col.visible)
                            .map((col) => (
                              <td key={col.key} className="py-3 px-6 text-left">
                                {renderTableTotal(col, filteredData)}
                              </td>
                            ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {/* Pagination Controls */}
                <div className="flex justify-end items-center mt-4 p-3">
                  <div className="flex items-center space-x-2 mr-4">
                    <label className="text-gray-700">Rows per page:</label>
                    <select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="25">25</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="p-1 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                    >
                      <FaChevronLeft />
                    </button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="p-1 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex justify-center items-center h-40 mt-8">
                <LoadingAnimation />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default BillingTable;
